import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'

const MIN_PASSWORD_LENGTH = 6

export default function ResetPassword() {
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleResetPassword = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!newPassword || !confirmPassword) {
      setErrorMessage("Please fill in both password fields.")
      return
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`)
      return
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.")
      return
    }

    setSubmitting(true)

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession()

    if (sessionError || !sessionData?.session) {
      setErrorMessage("Your reset link has expired or is invalid. Please request a new one.")
      setSubmitting(false)
      return
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (updateError) {
        setErrorMessage(updateError.message || "Could not update your password. Please try again.")
      } else {
        setSuccessMessage("✅ Password updated successfully. Redirecting to login...")
        setTimeout(() => {
          navigate('/login')
        }, 2500)
      }
    } catch (err) {
      console.error(err)
      setErrorMessage("A network error occurred. Please check your connection and try again.")
    }

    setSubmitting(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-softgray px-4">
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-md border border-gray-100">
        <h1 className="text-2xl font-bold text-center text-primary mb-6">🔐 Reset your password</h1>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-700 block mb-1">New password</label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(v => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-700 block mb-1">Confirm password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(v => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {errorMessage && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{errorMessage}</p>
          )}

          {successMessage && (
            <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded px-3 py-2">{successMessage}</p>
          )}

          <button
            onClick={handleResetPassword}
            disabled={submitting}
            className="w-full py-2 bg-primary hover:bg-opacity-90 text-white font-semibold rounded-md transition disabled:opacity-60"
          >
            {submitting ? 'Updating...' : 'Update password'}
          </button>
        </div>
      </div>
    </div>
  )
}
